import { apiClientJSON } from "../apiClient";

const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
type JsonObject = { [key: string]: JsonValue };

function transformKeys<T>(obj: JsonValue): T {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys) as T;
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce<JsonObject>((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = transformKeys((obj as JsonObject)[key]);
      return acc;
    }, {}) as T;
  }
  return obj as T;
}

export interface UnifiedWallet {
  id: string;
  userId: string;
  paymentConfigurationId: string;
  amountMinor: number;
  pendingAmountMinor: number;
  currency: string;
  legacyIds: number[];
}

export type TransactionReasonType =
  | "WALLET_TOPUP"
  | "RIDE_DEDUCTION"
  | "ADJUSTMENT"
  | "REFUND"
  | "BONUS"
  | "PENALTY";

export interface TransactionReason {
  type?: TransactionReasonType | string;
  description?: string;
  referenceId?: string;
  [key: string]: unknown;
}

export interface UnifiedTransaction {
  id: string;
  userId: string;
  paymentConfigurationId: string;
  amountMinor: number;
  type: "ADD" | "DEDUCT";
  reason: TransactionReason | null;
  status: string;
  creationDatetime: string;
}

export interface PendingTransaction {
  source: string;
  id: string;
  amountMinor: number;
  type: string;
  status: string;
  creationDatetime: string;
  paymentConfigurationId: string;
  reason?: TransactionReason | null;
}

export interface PendingSummary {
  walletPendingAmountMinor: number;
  walletProcessingAddSumMinor: number;
  hasMismatch: boolean;
}

export interface AdminWalletView {
  wallet: UnifiedWallet;
  fleetId: string | null;
  fleetName: string | null;
  adjustable: boolean;
  archived: boolean;
}

/**
 * Build a `paymentConfigurationId -> currency` map from a user's wallets, so a
 * transaction can be labelled in its own wallet's currency rather than one
 * collapsed wallet's. Each wallet has its own currency.
 */
export function buildCurrencyByConfig(
  wallets?: AdminWalletView[]
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const view of wallets ?? []) {
    if (view.wallet?.paymentConfigurationId) {
      map[view.wallet.paymentConfigurationId] = view.wallet.currency;
    }
  }
  return map;
}

export interface WalletData {
  userFound: boolean;
  migratedUser: boolean;
  userId: string | null;
  legacyUserId: number | null;
  paymentConfigurationId: string | null;
  organizationId: string | null;
  unifiedWallet: UnifiedWallet | null;
  // Every wallet the (migrated) user holds, one per payment configuration. Optional
  // so a dashboard build that lands before the backend deploy degrades gracefully.
  unifiedWallets?: AdminWalletView[];
  unifiedTransactions: UnifiedTransaction[];
  pendingTransactions?: PendingTransaction[];
  pendingSummary?: PendingSummary | null;
}

export const getWalletByLegacyUser = async (
  legacyUserId: number,
  cityId?: number,
  includeTransactions: boolean = true
): Promise<WalletData> => {
  const params = new URLSearchParams({
    legacy_user_id: legacyUserId.toString(),
    include_transactions: includeTransactions.toString(),
  });

  if (cityId !== undefined) {
    params.append("city_id", cityId.toString());
  }

  return apiClientJSON
    .get(`/backend/api/v1/admin/wallets/by-legacy-user?${params.toString()}`)
    .then((r) => transformKeys<WalletData>(r.data.body));
};

export interface AdjustWalletBalanceParams {
  userId: number | string;
  cityId?: number | string;
  amount: number;
  note?: string;
}

export const adjustWalletBalance = async (
  params: AdjustWalletBalanceParams
): Promise<void> => {
  await apiClientJSON.post(
    "/payment-service/dashboard/wallet/adjust-balance",
    params
  );
};

export type WalletAdjustType = "ADD" | "DEDUCT";

export interface AdjustUnifiedWalletParams {
  legacyUserId: number;
  paymentConfigurationId: string;
  amountMinor: number;
  type: WalletAdjustType;
  note: string;
  // Generated once per submit by the caller so a retried mutation is idempotent.
  idempotencyKey: string;
}

export interface AdjustUnifiedWalletResult {
  applied: boolean;
  wallet: UnifiedWallet;
}

// Direct, synchronous adjustment of a migrated user's unified wallet (LISA-428),
// keyed by payment configuration. Replaces the legacy city-keyed adjust-balance
// call for migrated users.
export const adjustUnifiedWallet = async (
  params: AdjustUnifiedWalletParams
): Promise<AdjustUnifiedWalletResult> => {
  return apiClientJSON
    .post("/backend/api/v1/admin/wallets/adjust", {
      legacy_user_id: params.legacyUserId,
      payment_configuration_id: params.paymentConfigurationId,
      amount_minor: params.amountMinor,
      type: params.type,
      note: params.note,
      idempotency_key: params.idempotencyKey,
    })
    .then((r) => transformKeys<AdjustUnifiedWalletResult>(r.data.body));
};
